import { useEffect, useState } from 'react';

const useMediaQueries = <T extends string>(queries: Record<T, string>): Record<T, boolean> & { default: boolean } => {
    const [matches, setMatches] = useState<Record<T, boolean> & { default: boolean }>(() => ({
        ...(Object.fromEntries(Object.keys(queries).map((name) => [name, false])) as Record<T, boolean>),
        default: false,
    }));
    const queriesString = JSON.stringify(queries);

    useEffect(() => {
        const controller = new AbortController();
        const parsedQueries = JSON.parse(queriesString) as Record<T, string>;

        const { defaultMatches, currentMatches, mediaQueries } = Object.entries<string>(parsedQueries).reduce(
            (acc, [name, query]) => {
                const mediaQuery = window.matchMedia(query);

                acc.defaultMatches[name] = false;
                acc.currentMatches[name] = mediaQuery.matches;
                acc.mediaQueries.push([name as T, mediaQuery]);

                return acc;
            },
            {
                defaultMatches: {} as Record<T, boolean>,
                currentMatches: {} as Record<T, boolean>,
                mediaQueries: [] as [T, MediaQueryList][],
            }
        );

        setMatches({ ...currentMatches, default: true });

        mediaQueries.forEach(([name, mediaQuery]) => {
            mediaQuery.addEventListener(
                'change',
                ({ matches }) => {
                    setMatches((prev) => ({
                        ...defaultMatches,
                        ...prev,
                        [name]: matches,
                    }));
                },
                { signal: controller.signal }
            );
        });

        return () => {
            controller.abort();
        };
    }, [
        // since "queries" is an object,
        // but useEffect uses `Object.is` for its comparison algorithm: https://react.dev/reference/react/useEffect#parameters,
        // we need to convert it to be a primitive value
        // to skip unnecessary MediaQueryList instance re-creations
        queriesString,
    ]);

    return matches;
};

export default useMediaQueries;
